import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {Paperclip, Loader2, CheckCircle2, XCircle, Eye, } from 'lucide-react'
import AdminLayout from '@/components/AdminLayout'
import { useCases, type WorkCase } from '@/hooks/useCases'
import { CASE_STATUS_META, categoryLabel } from '@/lib/caseCategories'

const FILTERS = [
  { value: 'todos', label: 'Todos' },
  { value: 'abierto', label: 'Abiertos' },
  { value: 'en_revision', label: 'En revisión' },
  { value: 'validado', label: 'Validados' },
  { value: 'rechazado', label: 'Rechazados' },
]

function AdminCasos() {
  const navigate = useNavigate()
  const { cases, loading, fetchAll, updateCase } = useCases()
  const [filter, setFilter] = useState('todos')
  const [active, setActive] = useState<WorkCase | null>(null)
  const [resolution, setResolution] = useState('')
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (!localStorage.getItem('beeznz_admin')) {
      navigate('/login')
      return
    }
    fetchAll()
  }, [])

  const openDetail = (c: WorkCase) => {
    setActive(c)
    setResolution(c.resolution || '')
  }

  const handleResolve = async (status: 'validado' | 'rechazado' | 'en_revision') => {
    if (!active) return
    setSaving(true)
    try {
      const adminId = localStorage.getItem('beeznz_admin') || 'admin'
      await updateCase(active._id, {
        status,
        resolution: resolution.trim(),
        reviewerUserId: adminId,
        resolvedAt: status === 'en_revision' ? undefined : new Date().toISOString(),
        addedToHistory: status === 'validado',
      })
      setActive(null)
      await fetchAll()
    } catch (e) {
      console.error('Error al resolver caso:', e)
    } finally {
      setSaving(false)
    }
  }

  const filtered = filter === 'todos' ? cases : cases.filter((c) => c.status === filter)
  const pending = cases.filter((c) => c.status === 'abierto' || c.status === 'en_revision').length

  return (
    <AdminLayout title="Casos y reportes laborales" subtitle={`${pending} por revisar`}>
          <div className="mb-5 flex flex-wrap gap-2">
            {FILTERS.map((f) => (
              <button
                key={f.value}
                onClick={() => setFilter(f.value)}
                className={`rounded-lg px-3 py-1.5 text-sm font-medium transition ${
                  filter === f.value ? 'bg-slate-900 text-amber-400' : 'bg-white text-slate-600 hover:bg-slate-100'
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>

          {loading && cases.length === 0 ? (
            <p className="text-sm text-slate-400">Cargando casos...</p>
          ) : filtered.length === 0 ? (
            <p className="rounded-xl border border-dashed border-slate-300 bg-white p-6 text-center text-sm text-slate-500">
              No hay casos en esta categoría.
            </p>
          ) : (
            <div className="space-y-3">
              {filtered.map((c) => {
                const meta = CASE_STATUS_META[c.status] || CASE_STATUS_META.abierto
                return (
                  <article key={c._id} className="rounded-xl border border-slate-200 bg-white p-4">
                    <div className="flex flex-wrap items-start justify-between gap-2">
                      <div className="min-w-0">
                        <h3 className="text-sm font-semibold text-slate-900">{c.title}</h3>
                        <p className="mt-0.5 text-xs text-slate-500">
                          {categoryLabel(c.category)} · {c.reporterName || 'N/D'} ({c.reporterRole}) → {c.targetName || 'N/D'}
                        </p>
                      </div>
                      <div className="flex shrink-0 items-center gap-2">
                        <span className={`rounded-md px-2.5 py-1 text-xs font-medium ${meta.cls}`}>{meta.label}</span>
                        <button
                          onClick={() => openDetail(c)}
                          className="inline-flex items-center gap-1 rounded-lg bg-slate-900 px-3 py-1.5 text-xs font-semibold text-amber-400 hover:bg-slate-800"
                        >
                          <Eye size={13} /> Revisar
                        </button>
                      </div>
                    </div>
                    <p className="mt-2 line-clamp-2 text-sm text-slate-600">{c.description}</p>
                  </article>
                )
              })}
            </div>
          )}

      {/* Detalle / revisión */}
      {active && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="flex max-h-[90vh] w-full max-w-lg flex-col overflow-hidden rounded-2xl bg-white">
            <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4">
              <h2 className="text-base font-semibold text-slate-900">Revisión de caso</h2>
              <button onClick={() => setActive(null)} className="rounded-lg p-1 text-slate-400 hover:bg-slate-100">
                <XCircle size={18} />
              </button>
            </div>
            <div className="flex-1 space-y-3 overflow-y-auto px-5 py-4">
              <div>
                <h3 className="text-sm font-semibold text-slate-900">{active.title}</h3>
                <p className="mt-0.5 text-xs text-slate-500">
                  {categoryLabel(active.category)} · {active.reporterName} ({active.reporterRole}) → {active.targetName}
                </p>
              </div>
              <p className="text-sm text-slate-600">{active.description}</p>

              {active.evidenceUrls && active.evidenceUrls.length > 0 && (
                <div className="space-y-1">
                  <p className="text-xs font-semibold text-slate-700">Evidencias</p>
                  {active.evidenceUrls.map((url, i) => (
                    <a
                      key={url}
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 text-xs text-amber-600 hover:underline"
                    >
                      <Paperclip size={12} /> Evidencia {i + 1}
                    </a>
                  ))}
                </div>
              )}

              <div>
                <label className="mb-1.5 block text-xs font-medium text-slate-700">Resolución / notas</label>
                <textarea
                  value={resolution}
                  onChange={(e) => setResolution(e.target.value)}
                  rows={3}
                  placeholder="Explica la decisión de Beeznz Work sobre este caso."
                  className="w-full resize-none rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-amber-400 focus:outline-none focus:ring-2 focus:ring-amber-100"
                />
              </div>
            </div>
            <div className="flex flex-wrap items-center justify-end gap-2 border-t border-slate-100 px-5 py-4">
              <button
                onClick={() => handleResolve('en_revision')}
                disabled={saving}
                className="rounded-lg px-3 py-2 text-sm font-medium text-amber-700 hover:bg-amber-50 disabled:opacity-60"
              >
                Marcar en revisión
              </button>
              <button
                onClick={() => handleResolve('rechazado')}
                disabled={saving}
                className="inline-flex items-center gap-1.5 rounded-lg border border-red-200 px-4 py-2 text-sm font-semibold text-red-600 hover:bg-red-50 disabled:opacity-60"
              >
                {saving ? <Loader2 size={14} className="animate-spin" /> : <XCircle size={14} />} Rechazar
              </button>
              <button
                onClick={() => handleResolve('validado')}
                disabled={saving}
                className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-500 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-600 disabled:opacity-60"
              >
                {saving ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />} Validar
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  )
}

export default AdminCasos
